/**
 * 通讯器 · TA 的手机（P2，用户 10-03 06:3x：「更应该像是一个手机那种展示的一个页面，聊天的……跟其他宠物的聊天、朋友……还有朋友圈。
 * 家庭那个我觉得是不需要的……更像是一个家庭的群……这个手机可能也会有一些小游戏」）。
 *
 * /communicator?channel=phone 是手机的首屏（信箱、地图上的通讯器按钮都进这里）：
 * - 最上面一行轻量的时间日期（看的人所在时区；10-03 撤掉原来那块大钟屏幕，把首屏还给聊天，c84a 0020Z 视觉复审第 1 条），
 *   下面是公告细条和星球活动入口（原来挂在聊天页顶上）；
 * - 聊天：我和 TA（点进 /communicator）、家庭群（点进 ?channel=family；一个人住的家没有这一栏，TA 写给全家的信都在“我和 TA”里）。
 *   每行写最后一句、时间、未读几条（首页快照的分区未读），没有消息就写一句该怎么开始，不编内容；
 *   下面接着是 TA 和朋友最近的几场聊天（./PetChatsSection：点进去是只读回放，主人旁观）。契约的列表没有原话，行里写聊得怎样、几句；
 *   多于 3 场给“TA 和朋友的全部聊天”；还没聊过、读不到就不列（不占位、不报错，全部聊天页里再说）；
 * - TA 的朋友们：最近往来的几位，点进那位朋友（TA 自己交的朋友，主人只看，不加不删）；多于 4 位给“全部 N 位朋友”；
 * - 手机里的应用：朋友圈、小游戏、相册。10-03 起不再贴底（贴底那条大横条在 320 宽压住列表，用户也不喜欢大底横条，
 *   c84a 0020Z 第 2 条），改成跟着页面走的一排，放在聊天后面、朋友前面。UI-DECLUTTER-01 去掉「朋友」：下面「TA 的朋友们」
 *   就是同一份名单；相册、小游戏从这里进（小窝、地图左侧栏不再放，用户 10-08）。
 * 看得见的字和读屏里不写 AI、模型、生成和内部代码。
 */
import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import type { HouseholdPetBrief, MessageSummary } from "@/shared/contracts";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { useNow } from "@/shared/time/clock";
import { ErrorState, Icon, PetAvatar, type IconName } from "@/shared/ui";
import { preserveSceneState, readSceneEntry, SourceLink } from "@/shared/ui/sceneNavigation";
import { PetPortrait } from "@/features/pets/PetPortrait";
import { AnnouncementStrip } from "../AnnouncementStrip";
import { PlanetEventsEntry } from "../PlanetEventsEntry";
import { friendDetailHref, friendRow } from "../petFriends";
import { usePetFriendList } from "../usePetFriends";
import { SameNameTag } from "@/shared/ui/SameNameTag";
import { sameNameTags } from "@/shared/ui/sameNameTags";
import { chatEndingLabel, chatStateOf } from "../petFriends";
import { FamilyGroupAvatar } from "./FamilyGroup";
import { PET_CHATS_HREF, petChatHref } from "./PetChatsSection";
import { chatTimeText, usePetChatList } from "./usePetChats";
import type { FamilyGroupView } from "./useFamilyGroup";
import { chatTime, lastMessage, messageAt, phoneClock, previewText, unreadBadge, type PhoneChat } from "./phoneLines";
import "./phone.css";

/** 首屏列几位朋友，再多给“全部 N 位朋友” */
export const PHONE_FRIENDS = 4;
/** 首屏列几场 TA 和朋友的聊天，再多给“全部聊天” */
export const PHONE_PET_CHATS = 3;

type PhonePet = Pick<HouseholdPetBrief, "pet_id" | "name" | "species" | "photo_url" | "origin">;

const APPS: Array<{ to: string; label: string; icon: IconName; illustration: string }> = [
  { to: "/circle", label: "朋友圈", icon: "comment", illustration: "/ui-assets/phone-entry-illustrations/v1/friends-feed.png" },
  { to: "/play", label: "小游戏", icon: "game", illustration: "/ui-assets/phone-entry-illustrations/v1/mini-game.png" },
  { to: "/album", label: "相册", icon: "album", illustration: "/ui-assets/phone-entry-illustrations/v1/album.png" },
];

/** 本地入口插画是装饰；同一挂载内失败后保持原图标，不随快照更新反复加载。 */
function PhoneAppIcon({ icon, illustration }: { icon: IconName; illustration: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="ps-phone-app__icon" aria-hidden="true">
      {failed ? <Icon name={icon} size={20} /> : (
        <img className="ps-phone-app__illustration" src={illustration} width={48} height={48} alt="" draggable={false} decoding="async" onError={() => setFailed(true)} />
      )}
    </span>
  );
}

function ChatRow({ to, avatar, title, chat, last, petName, unread, empty, now, testId }: {
  to: string;
  avatar: ReactNode;
  title: string;
  chat: PhoneChat;
  last: MessageSummary | null;
  petName: string;
  unread: number;
  empty: string;
  now: number;
  testId: string;
}) {
  return (
    <PhoneRow
      to={to}
      avatar={avatar}
      title={title}
      preview={previewText(last, chat, petName)}
      empty={empty}
      time={last ? chatTime(messageAt(last), now) : null}
      unread={unread}
      testId={testId}
    />
  );
}

/** 聊天列表的一行：头像、标题、时间、一行预览（没有就写 empty）、未读角标 */
function PhoneRow({ to, avatar, title, preview, empty, time, unread, testId }: {
  to: string;
  avatar: ReactNode;
  title: string;
  preview: string | null;
  empty: string;
  time: string | null;
  unread: number;
  testId: string;
}) {
  const badge = unreadBadge(unread);
  const spoken = [title, badge ? `${unread} 条未读` : null, preview ?? empty, time].filter(Boolean).join("，");
  return (
    <li>
      <Link className={`ps-phone-chat${badge ? " has-unread" : ""}`} to={to} aria-label={spoken} data-testid={testId}>
        <span className="ps-phone-chat__avatar" aria-hidden="true">
          {avatar}
        </span>
        <span className="ps-phone-chat__main">
          <span className="ps-phone-chat__top">
            <strong className="ps-phone-chat__title">{title}</strong>
            {time ? <span className="ps-phone-chat__time">{time}</span> : null}
          </span>
          <span className={`ps-phone-chat__preview${preview ? "" : " is-empty"}`}>{preview ?? empty}</span>
        </span>
        {badge ? (
          <span className="ps-phone-chat__badge" aria-hidden="true" data-testid={`${testId}-badge`}>
            {badge}
          </span>
        ) : null}
      </Link>
    </li>
  );
}

/** TA 和朋友最近的几场聊天：一场一行，点进回放；没有、读不到就不列 */
function PetChatRows({ petId, now }: { petId: string | null; now: number }) {
  const { query, items } = usePetChatList(petId);
  if (!items.length) return null;
  const at = new Date(now);
  return (
    <>
      {items.slice(0, PHONE_PET_CHATS).map((item) => {
        const state = chatStateOf(item);
        const preview = [state?.label, chatEndingLabel(item), item.line_count > 0 ? `${item.line_count} 句` : null].filter(Boolean).join(" · ");
        return (
          <PhoneRow
            key={item.thread_id}
            to={petChatHref(item.thread_id)}
            avatar={<PetPortrait name={item.counterpart.name} species={item.counterpart.species} size={48} />}
            title={item.counterpart.name}
            preview={preview || null}
            empty="点开看看聊了什么"
            time={chatTimeText(item.ended_at ?? item.started_at, at)}
            unread={0}
            testId="phone-pet-chat"
          />
        );
      })}
      {items.length > PHONE_PET_CHATS || query.hasNextPage ? (
        <li>
          <Link className="ps-phone-more ps-phone-more--row" to={PET_CHATS_HREF}>
            TA 和朋友的全部聊天
            <Icon name="chevron" size={14} />
          </Link>
        </li>
      ) : null}
    </>
  );
}

function PhoneFriends({ petId }: { petId: string | null }) {
  const location = useLocation();
  const phone = "/communicator?channel=phone";
  const inherited = readSceneEntry(location.state, phone);
  const entryState = { ...preserveSceneState(location.state), sceneEntry: { version: 1, parent: phone, origin: inherited?.origin ?? phone } };
  const friends = usePetFriendList(petId);
  let body: ReactNode;
  if (friends.isError) {
    const error = toApiError(friends.error);
    body = (
      <p className="ps-phone-note">
        {(error.isCapabilityUnavailable || error.unknownRoute) && env.dataMode === "fixture" ? "演示里还没有朋友记录。" : "暂时看不到 TA 的朋友，过一会儿再来看看。"}
      </p>
    );
  } else if (!friends.data) {
    body = <p className="ps-phone-note">翻看 TA 的朋友…</p>;
  } else {
    const rows = (friends.data.items ?? []).map((friend) => friendRow(friend));
    // 同一屏同名时居民那一边加小字（SAME-NAME-LABEL-117，属主 6c2b 10-16 授权）：只算这一屏露出来的几位；
    // 星球居民（kind=resident）认得出；kind=pet 里可能有驿站居民，前端分不出，不猜（null），等后端补 is_resident
    const tags = sameNameTags(rows.slice(0, PHONE_FRIENDS).map((row) => ({ id: row.id, name: row.name, resident: row.resident ? true : null })));
    body = rows.length ? (
      <>
        <ul className="ps-phone-friends" aria-label="TA 最近往来的朋友">
          {rows.slice(0, PHONE_FRIENDS).map((row) => {
            const href = friendDetailHref(row.id);
            const sub = [row.exchange?.label, row.when].filter(Boolean).join(" · ");
            const inner = (
              <>
                <span className="ps-phone-friend__avatar" aria-hidden="true">
                  <PetPortrait name={row.name} size={40} />
                </span>
                <span className="ps-phone-friend__main">
                  <strong>{row.name}<SameNameTag tag={tags.get(row.id)} /></strong>
                  {sub ? <span>{sub}</span> : null}
                </span>
              </>
            );
            return (
              <li key={row.id}>
                {href ? (
                  <Link className="ps-phone-friend" to={`${href}?from=phone`} state={entryState} aria-label={row.spoken} data-testid="phone-friend">
                    {inner}
                    <Icon name="chevron" size={16} className="ps-phone-friend__go" />
                  </Link>
                ) : (
                  <div className="ps-phone-friend" data-testid="phone-friend">
                    {inner}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {rows.length > PHONE_FRIENDS ? (
          <Link className="ps-phone-more" to="/communicator?channel=friends">
            全部 {rows.length} 位朋友
            <Icon name="chevron" size={14} />
          </Link>
        ) : null}
      </>
    ) : (
      <p className="ps-phone-note">TA 还没在外面遇到朋友。出门时也许会遇见合得来的。</p>
    );
  }
  return (
    <section className="ps-phone-block" aria-labelledby="ps-phone-friends-title">
      <h2 className="ps-phone-block__title" id="ps-phone-friends-title">
        TA 的朋友们
      </h2>
      {body}
    </section>
  );
}

export function PhoneHome({ pet, petId, name, solo, group, items, unreadMine, unreadFamily, readError, retryRead, retrying, identityReady = true }: {
  readError?: unknown;
  retryRead?: () => void;
  retrying?: boolean;
  identityReady?: boolean;
  /** 头像用：家庭名单里的这一只（演示没有家庭上下文时由家园快照凑） */
  pet: PhonePet | null;
  petId: string | null;
  name: string;
  /** 一个人住的家：没有家庭群这一栏 */
  solo: boolean;
  /** 家庭群：群名、群里的伙伴（通讯器页取一次，家庭群顶栏也用） */
  group: FamilyGroupView;
  /** 已经读到的消息（最新一页加往上翻过的）；还没读到是 null */
  items: readonly MessageSummary[] | null;
  unreadMine: number;
  unreadFamily: number;
}) {
  const now = useNow(30_000);
  const clock = phoneClock(now);
  const avatar = pet ? <PetAvatar petId={pet.pet_id} name={pet.name} species={pet.species} photoUrl={pet.photo_url} origin={pet.origin} size={48} /> : <PetPortrait name={name} size={48} />;
  return (
    <div className="ps-phone" data-testid="phone-home">
      <section className="ps-phone-status" aria-label={`${name}的手机，${clock.time}，${clock.date}`}>
        <span className="ps-phone-status__time" aria-hidden="true">
          {clock.time}
        </span>
        <span aria-hidden="true">{clock.date}</span>
      </section>
      <div className="ps-comm-notices ps-phone-notices">
        <AnnouncementStrip />
        <PlanetEventsEntry />
      </div>
      <section className="ps-phone-block" aria-labelledby="ps-phone-chats-title">
        <h2 className="ps-phone-block__title" id="ps-phone-chats-title">
          聊天
        </h2>
        {readError ? <ErrorState error={readError} onRetry={retryRead} retrying={retrying} /> : null}
        <ul className="ps-phone-chats">
          <ChatRow
            to="/communicator"
            avatar={avatar}
            title={`我和 ${name}`}
            chat="private"
            last={lastMessage(items, "private", solo)}
            petName={name}
            unread={unreadMine}
            empty={!identityReady ? "请先确认当前账号与伙伴" : readError ? "来信暂时没读到" : items === null ? "正在翻看…" : "给 TA 捎句话，TA 会按自己的状态晚点回你"}
            now={now}
            testId="phone-chat-private"
          />
          {solo ? null : (
            <ChatRow
              to="/communicator?channel=family"
              avatar={<FamilyGroupAvatar pets={group.pets} size={48} />}
              title={group.householdName ?? "家庭来信"}
              chat="family"
              last={lastMessage(items, "family", solo)}
              petName={name}
              unread={unreadFamily}
              empty={!identityReady ? "请先确认当前账号与伙伴" : readError ? "来信暂时没读到" : items === null ? "正在翻看…" : "TA 写给全家的信会到这里"}
              now={now}
              testId="phone-chat-family"
            />
          )}
          <PetChatRows petId={petId} now={now} />
        </ul>
      </section>
      <nav className="ps-phone-block ps-phone-apps" aria-label="TA 手机里的应用">
        <h2 className="ps-phone-block__title" aria-hidden="true">
          应用
        </h2>
        <div className="ps-phone-apps__grid">
          {APPS.map((app) => (
            // UI-DECLUTTER-01：带上来路（手机），从相册、小游戏返回时回到手机
            <SourceLink key={app.to} className="ps-phone-app" to={app.to}>
              <PhoneAppIcon icon={app.icon} illustration={app.illustration} />
              <span className="ps-phone-app__label">{app.label}</span>
            </SourceLink>
          ))}
        </div>
      </nav>
      <PhoneFriends petId={petId} />
    </div>
  );
}
